import { isEmpty } from '@ember/utils';

/**
 * SchoolStationeryLinks stores a group of school stationery links, bucketed
 * based on:
 *  - lists: Stationery for a class, homeroom, subject or year level.
 *  - packs: A bundle of stationery for a whole year group. For example, Year 1's all need the same stationery.
 */
export interface SchoolStationeryLinks {
  lists: SchoolStationeryLink[];
  packs: SchoolStationeryLink[];
}

/**
 * SchoolStationeryLink stores information about a given school stationery link.
 */
export interface SchoolStationeryLink {
  // The name of the related stationery grouping.
  label: string;
  // The direct URL to where the stationery link is found.
  link: string;
}

/**
 * getStationeryLinks given a school id will return parsed links for a school.
 */
export function getStationeryLinks(
  // TS-STANDIN(school): the school option that holds the stationery JSON.
  school: { warehouseStationeryJson?: string | null } | null | undefined,
): SchoolStationeryLinks | undefined {
  if (!school || isEmpty(school)) {
    // no school, no linky~links for you!
    return undefined;
  }

  // Attempt to obtain school option and parse stored JSON.
  return parseJsonStationeryLinks(school.warehouseStationeryJson);
}

/**
 * parseJsonStationeryLinks takes in SchoolStationeryLinks json or returns an
 * empty SchoolStationeryLinks object if the json is malformed or empty.
 *
 * @param json - A JSON string that conforms to the {SchoolStationeryLinks} type.
 */
export function parseJsonStationeryLinks(
  json: string | null | undefined,
): SchoolStationeryLinks | undefined {
  // Attempt to obtain school option and parse stored JSON.
  if (!json || isEmpty(json)) {
    // If the option exists but content doesn't, return undefined for template
    // detection of enrollment in stationery programme.
    return undefined;
  }

  try {
    // Not validated: the stored JSON is assumed to match the type.
    return JSON.parse(json) as SchoolStationeryLinks;
  } catch {
    // malformed JSON, pretend we've got no data so the school doesn't look
    // un-enrolled...
    return defaultStationery();
  }
}

/**
 * defaultStationery provides an empty stationery model.
 */
function defaultStationery(): SchoolStationeryLinks {
  return {
    lists: [],
    packs: [],
  };
}
